import { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Skeleton } from '../ui/skeleton';
import {
  Buildings, Phone, EnvelopeSimple, IdentificationCard, Star, MapPin,
  Clock, Briefcase, FileText, Envelope, SignIn, CheckCircle, CircleDashed,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function fmtDate(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return s; }
}
function fmtDateTime(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return s; }
}
function fmtEUR(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
}

export default function FicheEntrepriseModal({ open, onOpenChange, entrepriseId }) {
  const [fiche, setFiche] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !entrepriseId) return;
    setLoading(true);
    axios.get(`${API_URL}/api/entreprises/${entrepriseId}/fiche-complete`, { withCredentials: true })
      .then(({ data }) => setFiche(data))
      .catch(() => setFiche(null))
      .finally(() => setLoading(false));
  }, [open, entrepriseId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="fiche-entreprise-modal">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }} className="flex items-center gap-2">
            <Buildings size={22} className="text-[#C75B39]" />
            {loading ? 'Chargement...' : fiche?.infos?.nom || 'Fiche entreprise'}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Fiche complète d'une entreprise : informations, compte utilisateur, chantiers associés, documents et historique DCE.
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="space-y-3"><Skeleton className="h-24" /><Skeleton className="h-24" /></div>}

        {fiche && !loading && (
          <div className="space-y-6 mt-2">
            {/* Infos de base */}
            <section>
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#6B6B6B] mb-3">Informations</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <InfoRow icon={<IdentificationCard size={16} />} label="SIRET" value={fiche.infos.siret || '—'} />
                <InfoRow icon={<Briefcase size={16} />} label="Activité" value={fiche.infos.activite || '—'} />
                <InfoRow icon={<EnvelopeSimple size={16} />} label="Email" value={fiche.infos.email || '—'} />
                <InfoRow icon={<Phone size={16} />} label="Téléphone" value={fiche.infos.telephone || '—'} />
                <InfoRow icon={<MapPin size={16} />} label="Adresse" value={fiche.infos.adresse || '—'} />
                <InfoRow icon={<Star size={16} />} label="Évaluation" value={fiche.infos.evaluation ? `${fiche.infos.evaluation}/5` : '—'} />
                {fiche.infos.contact_principal && (
                  <InfoRow icon={<IdentificationCard size={16} />} label="Contact" value={fiche.infos.contact_principal} />
                )}
                {fiche.infos.source && (
                  <InfoRow icon={<FileText size={16} />} label="Source" value={fiche.infos.source} />
                )}
              </div>
              {fiche.infos.notes && (
                <div className="mt-3 p-3 bg-[#FAFAF8] border-l-2 border-[#C75B39] text-sm text-[#2C2C2C] italic">
                  {fiche.infos.notes}
                </div>
              )}
            </section>

            {/* Compte utilisateur */}
            <section>
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#6B6B6B] mb-3">Compte utilisateur</h3>
              {fiche.compte_utilisateur ? (
                <div className="border border-[#E0E0DB] p-4 bg-white" data-testid="fiche-compte-utilisateur">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                    <InfoRow icon={<CheckCircle size={16} weight="fill" className="text-[#2D6A4F]" />} label="Statut" value={(fiche.compte_utilisateur.is_active ?? fiche.compte_utilisateur.active) !== false ? 'Actif' : 'Désactivé'} />
                    <InfoRow icon={<Clock size={16} />} label="Dernière connexion" value={fmtDateTime(fiche.compte_utilisateur.last_login)} />
                    <InfoRow icon={<SignIn size={16} />} label="Nb connexions" value={fiche.compte_utilisateur.login_count ?? 0} />
                  </div>
                </div>
              ) : (
                <div className="border border-dashed border-[#E0E0DB] p-4 text-sm text-[#6B6B6B] flex items-center gap-2">
                  <CircleDashed size={16} /> Aucun compte utilisateur associé (pas de connexion possible)
                </div>
              )}
            </section>

            {/* Chantiers associés */}
            <section>
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#6B6B6B] mb-3">
                Chantiers associés ({fiche.nb_chantiers})
              </h3>
              {fiche.chantiers_associes.length === 0 ? (
                <p className="text-sm text-[#9A9A9A] italic">Aucun chantier associé pour le moment</p>
              ) : (
                <div className="space-y-2">
                  {fiche.chantiers_associes.map(c => (
                    <div key={c.chantier_id} className="border border-[#E0E0DB] p-3 hover:border-[#C75B39] transition-colors" data-testid={`fiche-chantier-${c.chantier_id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <a href={`/chantier/${c.chantier_id}`} className="text-sm font-semibold text-[#1E3A5F] hover:underline">
                            {c.nom}
                          </a>
                          {c.adresse && <p className="text-xs text-[#6B6B6B] mt-0.5">{c.adresse}</p>}
                          <div className="flex flex-wrap gap-1 mt-2">
                            {c.lots.map((l, i) => (
                              <span key={i} className={`text-[10px] px-2 py-0.5 border ${l.statut === 'attribue' ? 'border-[#2D6A4F] text-[#2D6A4F] bg-[#E8F5E9]' : 'border-[#E0E0DB] text-[#6B6B6B]'}`}>
                                {l.code || l.nom} {l.statut === 'attribue' ? '· ✓' : '· en attente'}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="text-right text-sm font-semibold text-[#2C2C2C] whitespace-nowrap">
                          {fmtEUR(c.montant_total_attribue)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Documents */}
            <section>
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#6B6B6B] mb-3">
                Documents déposés ({fiche.documents.length})
              </h3>
              {fiche.documents.length === 0 ? (
                <p className="text-sm text-[#9A9A9A] italic">Aucun document (Kbis, attestations assurances…)</p>
              ) : (
                <div className="space-y-1">
                  {fiche.documents.map(d => (
                    <div key={d.id} className="flex items-center gap-3 text-sm border border-[#E0E0DB] px-3 py-2" data-testid={`fiche-document-${d.id}`}>
                      <FileText size={16} className="text-[#C75B39]" />
                      <span className="font-medium text-[#2C2C2C]">{d.type}</span>
                      <span className="text-[#6B6B6B] truncate flex-1">{d.nom || d.filename}</span>
                      <span className="text-xs text-[#9A9A9A] whitespace-nowrap">
                        {fmtDate(d.date_upload)}
                        {d.date_expiration && <> · expire le {fmtDate(d.date_expiration)}</>}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Invitations DCE */}
            {fiche.invitations_dce.length > 0 && (
              <section>
                <h3 className="text-xs font-bold uppercase tracking-wider text-[#6B6B6B] mb-3">
                  Historique DCE ({fiche.invitations_dce.length} dernière(s))
                </h3>
                <div className="space-y-1">
                  {fiche.invitations_dce.map(i => (
                    <div key={i.invitation_id} className="flex items-center gap-3 text-xs border border-[#E0E0DB] px-3 py-2">
                      <Envelope size={14} className="text-[#6B6B6B]" />
                      <span className="text-[#2C2C2C] truncate flex-1">{(i.lots || []).join(', ') || 'Consultation'}</span>
                      <span className={`px-2 py-0.5 ${i.statut === 'REPONDUE' ? 'bg-[#E8F5E9] text-[#2D6A4F]' : i.statut === 'EN_ATTENTE' ? 'bg-[#FFF8E1] text-[#D4880F]' : 'bg-[#F5F5F3] text-[#6B6B6B]'}`}>
                        {i.statut}
                      </span>
                      <span className="text-[#9A9A9A] whitespace-nowrap">{fmtDate(i.date_envoi)}</span>
                      {i.montant_total_soumis != null && <span className="font-medium">{fmtEUR(i.montant_total_soumis)}</span>}
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-[#6B6B6B] mt-0.5">{icon}</span>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wider text-[#9A9A9A]">{label}</p>
        <p className="text-sm text-[#2C2C2C] truncate">{value}</p>
      </div>
    </div>
  );
}
